import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { createOrgUnit } from "@/services/appBookApi";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const MAX_TOKEN_BYTES = 50 * 1024;

export function AddAppBookDialog({ open, onOpenChange, onSuccess }: Props) {
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setName("");
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = (next: boolean) => {
    if (submitting) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      return;
    }
    if (f.size === 0) {
      toast.error("The selected token file is empty");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_TOKEN_BYTES) {
      toast.error("Token file is too large");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2 || trimmed.length > 64) {
      toast.error("Friendly name must be 2–64 characters");
      return;
    }
    if (!file) {
      toast.error("Please select a .vpptoken file");
      return;
    }
    setSubmitting(true);
    try {
      await createOrgUnit(trimmed, file);
      toast.success("Organisation unit added");
      reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to add organisation unit";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Apps &amp; Books Organisation Unit</DialogTitle>
          <DialogDescription>
            Give this organisation unit a friendly name and upload the{" "}
            <code className="text-xs">.vpptoken</code> file downloaded from Apple Business
            Manager.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="appbook-name">Friendly name</Label>
            <Input
              id="appbook-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Smplify.com"
              disabled={submitting}
              maxLength={64}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="appbook-token">Content token</Label>
            <Input
              ref={fileInputRef}
              id="appbook-token"
              type="file"
              accept=".vpptoken"
              onChange={handleFileChange}
              disabled={submitting}
            />
            {file && (
              <p className="text-xs text-muted-foreground">
                Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !file || !name.trim()}>
            {submitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            Upload token
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
